import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';

type SuggestedAction = 'upgrade_sme' | 'upgrade_agency' | 'convert_to_agency' | 'contact_support';

interface Props {
  open: boolean;
  existingCompany: { id: string; name: string; planId: string | null } | null;
  reason?: 'sme_admin_exists' | 'agency_client_cap_reached';
  capInfo?: { used: number; cap: number; agencyPlanId: string };
  suggestedActions?: SuggestedAction[];
  onClose: () => void;
}

export function UpgradeInsteadOfCreateModal({
  open, existingCompany, reason, capInfo, suggestedActions, onClose,
}: Props) {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const isAgencyCap = reason === 'agency_client_cap_reached';
  // v3.2 polish (21/05/26 ~22:20): the "next tier" suggestion was hard-
  // wired to Professional which skipped Medium (the previous code was
  // written when Medium didn't exist in v3.1). Now driven by a tier→
  // next-tier map so Starter→Medium / Medium→Professional / Professional→
  // Enterprise (contact sales) all render correctly.
  const TIER_LABEL: Record<string, string> = {
    agency_starter: 'Starter',
    agency_medium: 'Medium',
    agency_professional: 'Professional',
  };
  const NEXT_TIER: Record<string, { tier: 'agency_medium' | 'agency_professional'; cap: number } | null> = {
    agency_starter: { tier: 'agency_medium', cap: 10 },
    agency_medium: { tier: 'agency_professional', cap: 20 },
    agency_professional: null, // → contact sales (Enterprise)
  };
  const currentTier = capInfo?.agencyPlanId ?? '';
  const currentTierLabel = TIER_LABEL[currentTier] ?? currentTier;
  const nextInfo = NEXT_TIER[currentTier] ?? null;
  const isAtTopPaidTier = isAgencyCap && nextInfo === null;
  const nextTierLabel = nextInfo ? TIER_LABEL[nextInfo.tier] : '';
  const nextTierCap = nextInfo?.cap ?? 0;

  const planLabel = existingCompany?.planId
    ? existingCompany.planId.charAt(0).toUpperCase() + existingCompany.planId.slice(1)
    : '—';

  // Title + body
  let title: string;
  let body: string;
  if (isAgencyCap && isAtTopPaidTier && capInfo) {
    title = t('upgrade.agencyProCap.title');
    body = t('upgrade.agencyProCap.body', { used: capInfo.used, cap: capInfo.cap });
  } else if (isAgencyCap && capInfo && nextInfo) {
    title = t('upgrade.agencyTieredCap.title');
    body = t('upgrade.agencyTieredCap.body', {
      used: capInfo.used,
      cap: capInfo.cap,
      currentLabel: currentTierLabel,
      nextLabel: nextTierLabel,
      nextCap: nextTierCap,
    });
  } else {
    title = t('upgrade.smeCap.title');
    body = t('upgrade.smeCap.body', { name: existingCompany?.name ?? '' });
  }

  // v3.2 (21/05 19:27) — simplified CTA logic per CEO feedback.
  // Nel modello v3.2 NESSUN piano SME permette > 1 azienda (companies cap = 1
  // su Trial/Starter/Growth/Professional). L'unica risposta sensata al
  // "Hai gi\u00e0 un'azienda, vuoi crearne un'altra?" \u00e8 convertirsi ad Agency.
  // Quindi droppiamo `upgrade_sme` (irrelevant) e `contact_support` (overkill)
  // dal default — restano solo: Annulla + Diventa Agency.
  //
  // Per Agency cap reached il flow rimane: upgrade al tier successivo
  // (Starter→Medium / Medium→Professional) oppure contact_sales per
  // Agency Professional (Enterprise).
  const defaultActions: SuggestedAction[] = isAtTopPaidTier
    ? ['contact_support']
    : isAgencyCap
      ? ['upgrade_agency', 'contact_support']
      : ['convert_to_agency'];
  const actions = isAgencyCap && suggestedActions && suggestedActions.length > 0
    ? suggestedActions
    : defaultActions;

  // v3.2 (21/05 19:27): URL aggiornato a /profile?tab=billing
  // (Subscription tab spostata in Fase 4B.2). Legacy fallback redirect
  // in SettingsPage gestisce comunque /settings?tab=subscription.
  const goSubscription = () => { onClose(); navigate('/profile?tab=billing'); };

  const renderActionButton = (action: SuggestedAction) => {
    switch (action) {
      case 'upgrade_sme':
        return (
          <Button key={action} onClick={goSubscription}>
            {t('upgrade.cta.upgradeSme')}
          </Button>
        );
      case 'upgrade_agency':
        return (
          <Button key={action} onClick={goSubscription}>
            {nextTierLabel
              ? t('upgrade.cta.upgradeAgencyNext', { nextLabel: nextTierLabel })
              : t('upgrade.cta.upgradeAgency')}
          </Button>
        );
      case 'convert_to_agency':
        return (
          <Button key={action} variant="outline" onClick={goSubscription}>
            {t('upgrade.cta.convertToAgency')}
          </Button>
        );
      case 'contact_support': {
        const subject = encodeURIComponent('Lift account cap');
        const bodyMail = encodeURIComponent(`Account / company id: ${existingCompany?.id ?? '-'}`);
        return (
          <Button key={action} variant="ghost" asChild>
            <a href={`mailto:support@hiromatch.com?subject=${subject}&body=${bodyMail}`}>
              {t('upgrade.cta.contactSupport')}
            </a>
          </Button>
        );
      }
      default:
        return null;
    }
  };

  // Width scales with the number of CTAs the BE suggested:
  //   1 action  → max-w-md  (448px) — single CTA fits comfortably
  //   2 actions → max-w-lg  (512px)
  //   3+ actions → max-w-2xl (672px) — Cancel + 3 primary CTAs need room
  // (Feedback Alessandro 2026-05-14: SME flow had 4 buttons squashed at max-w-md.)
  const totalButtons = actions.length + 1; // +1 for Cancel
  const widthClass =
    totalButtons >= 4 ? 'max-w-2xl' : totalButtons === 3 ? 'max-w-lg' : 'max-w-md';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className={widthClass}>
        <DialogHeader>
          <div className="mx-auto h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center mb-2">
            <AlertTriangle className="h-6 w-6 text-orange-600" />
          </div>
          <DialogTitle className="text-center">{title}</DialogTitle>
          <DialogDescription className="text-center">{body}</DialogDescription>
        </DialogHeader>

        {!isAgencyCap && existingCompany && (
          <div className="rounded-md border border-border bg-muted/30 px-4 py-3">
            <p className="text-sm font-medium text-foreground truncate">{existingCompany.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t('onboarding.upgradeInsteadOfCreate.planLabel')}: {planLabel}
            </p>
          </div>
        )}

        {/* Stack vertically on mobile, wrap horizontally on sm+ to avoid overflow when 3+ CTAs are present. */}
        <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {actions.map(renderActionButton)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default UpgradeInsteadOfCreateModal;